import { useState, useEffect } from "react";
import LandingPage from "./LandingPage";
import Pricing from "./Pricing";
import Checkout from "./Checkout";
import Login from "./Login";
import Home from "./Home";
import AdminDashboard from "./AdminDashboard";
import UserManagement from "./UserManagement";
import StaffManagement from "./StaffManagement";
import CardManagement from "./CardManagement";
import OrgManagement from "./OrgManagement";
import GenerateQR from "./GenerateQR";
import ScanCard from "./ScanCard";
import RegisterCard from "./RegisterCard";
import CardCustomization from "./CardCustomization";
import MailScenarios from "./MailScenarios";
import StatsDashboard from "./StatsDashboard";
import MyAccount from "./MyAccount";
import QrRedirect, { isIos } from "./QrRedirect";
import QrLinks from "./QrLinks";
import TestCard from "./TestCard";
import PaymentReturn from "./PaymentReturn";
import BillingTest from "./BillingTest";
import Invoices from "./Invoices";
import Coupons from "./Coupons";
import Billing from "./Billing";
import InvitationAccept from "./InvitationAccept";
import { LegalPage, NotFound } from "./LegalPages";
import { apiGet, apiFetch, setUnauthorizedHandler } from "./api";

function App() {
  const path = window.location.pathname;
  const registerMatch = path.match(/^\/register\/(\d+)/);
  if (registerMatch) {
    const params = new URLSearchParams(window.location.search);
    // Sans ?platform= (QR unique affiché en magasin), on détecte l'appareil
    // du client ; le paramètre reste honoré pour les anciens QR imprimés.
    const platform = params.get("platform") || (isIos() ? "apple" : "android");
    return <RegisterCard orgId={registerMatch[1]} platform={platform} />;
  }

  // QR dynamique scanné : placeholder ou redirection vers l'enrôlement
  const qrMatch = path.match(/^\/q\/([\w-]+)/);
  if (qrMatch) {
    return <QrRedirect qrId={qrMatch[1]} />;
  }

  // Invitation reçue par e-mail : création du compte gérant
  const inviteMatch = path.match(/^\/invitation\/([\w-]+)/);
  if (inviteMatch) {
    return <InvitationAccept token={inviteMatch[1]} />;
  }

  // Retour depuis Stripe Checkout (success_url / cancel_url)
  if (path.startsWith("/paiement/succes")) {
    return <PaymentReturn status="success" />;
  }
  if (path.startsWith("/paiement/annule")) {
    return <PaymentReturn status="cancel" />;
  }

  // Pages légales publiques (liens du footer, URL directes)
  if (path === "/mentions-legales") {
    return <LegalPage page="mentions" />;
  }
  if (path === "/confidentialite") {
    return <LegalPage page="privacy" />;
  }
  if (path === "/cgv") {
    return <LegalPage page="cgv" />;
  }

  // Toute autre URL : vraie page 404. La navigation interne de MainApp ne
  // change jamais l'URL — seule la racine est donc légitime ici ; avant ce
  // garde, n'importe quel chemin cassé affichait la landing en 200 (soft-404).
  if (path !== "/") {
    return <NotFound />;
  }

  return <MainApp />;
}

// Titre d'onglet par écran : sans URL par vue, c'est le seul repère dans
// l'historique et les onglets du navigateur.
const PAGE_TITLES = {
  landing: "RACINES — Cartes de fidélité digitales",
  home: "Gestion — RACINES",
  scan: "Scanner une carte — RACINES",
  customize: "Personnalisation — RACINES",
  stats: "Statistiques — RACINES",
  "mail-scenarios": "Scénarios d'e-mails — RACINES",
  staff: "Employés — RACINES",
  billing: "Facturation — RACINES",
  subscription: "Abonnement — RACINES",
  account: "Mon compte — RACINES",
  admin: "Administration — RACINES",
  users: "Utilisateurs — RACINES",
  cards: "Cartes — RACINES",
  organizations: "Organisations — RACINES",
  "generate-qr": "QR d'enrôlement — RACINES",
  "qr-links": "QR dynamiques — RACINES",
  "test-card": "Banc d'essai cartes — RACINES",
  invoices: "Factures — RACINES",
  coupons: "Codes de réduction — RACINES",
};

// Vue de navigation par défaut. La vue complète est sérialisée dans
// l'historique du navigateur (history.state) : le bouton retour restaure la
// vue précédente au lieu de quitter le site.
const DEFAULT_VIEW = {
  page: "landing",
  showLogin: false,
  showPricing: false,
  checkoutPlan: null,
};

function MainApp() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState(DEFAULT_VIEW);
  const [loading, setLoading] = useState(true);
  const { page, showLogin, showPricing, checkoutPlan } = view;

  // Toute navigation interne passe par ici : applique le changement de vue et
  // pousse une entrée d'historique pour que « retour » revienne à la vue
  // précédente (l'URL, elle, ne change pas).
  const navigate = (patch) => {
    const target = { ...view, ...patch };
    setView(target);
    window.history.pushState({ fidelityView: target }, "");
  };
  const goPage = (p) => navigate({ page: p });
  // Menu burger depuis une page publique : rejoint l'écran demandé en
  // refermant les vues offres/commande/connexion en cours
  const goMenu = (p) =>
    navigate({ page: p, showLogin: false, showPricing: false, checkoutPlan: null });

  useEffect(() => {
    // Étiquette l'entrée d'historique initiale, pour qu'un retour jusqu'à
    // elle restaure aussi la bonne vue.
    window.history.replaceState({ fidelityView: DEFAULT_VIEW }, "");
    const onPop = (e) => {
      if (e.state && e.state.fidelityView) setView(e.state.fidelityView);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Chaque changement d'écran (page interne, offres, connexion, tunnel de
  // vente) ramène en haut : sans changement d'URL, le navigateur ne le fait
  // pas de lui-même et on hériterait du défilement de l'écran précédent.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page, showLogin, showPricing, checkoutPlan]);

  // Titre de l'onglet suivant l'écran affiché (les vues superposées —
  // offres, commande, connexion — priment sur la page de fond).
  useEffect(() => {
    let title = PAGE_TITLES[page] || "RACINES";
    if (showPricing) title = "Nos offres — RACINES";
    if (checkoutPlan) title = "Commande — RACINES";
    if (showLogin) title = "Connexion — RACINES";
    document.title = title;
  }, [page, showLogin, showPricing, checkoutPlan]);

  useEffect(() => {
    apiGet("/api/me")
      .then(setUser)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Session expirée ou révoquée pendant la navigation : on repasse par la
  // connexion au lieu de laisser les écrans afficher des listes vides.
  // replaceState (et non pushState) : l'entrée courante devient l'écran de
  // connexion, pas de nouvelle entrée pour une navigation subie.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      const target = { ...DEFAULT_VIEW, showLogin: true };
      setUser(null);
      setView(target);
      window.history.replaceState({ fidelityView: target }, "");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  if (loading) {
    return <div className="app-loading">Chargement...</div>;
  }

  const logout = () => {
    apiFetch("/api/logout", { method: "POST" })
      .catch(() => {})
      .finally(() => {
        setUser(null);
        navigate(DEFAULT_VIEW);
      });
  };

  // Les commerciaux n'ont pas accès aux fonctions commerçants (scan,
  // personnalisation, stats…), mais ils peuvent ouvrir le tunnel de vente
  // (landing, offres, contact) pour le montrer à un client : ces vues passent
  // au travers du bloc et sont rendues plus bas, comme pour les autres rôles.
  if (user && user.role === "Sale" && !checkoutPlan && !showPricing && page !== "landing") {
    if (page === "account") {
      return (
        <MyAccount
          user={user}
          onNavigate={goPage}
          onLogout={logout}
          onUserUpdate={setUser}
        />
      );
    }
    if (page === "qr-links") {
      return <QrLinks user={user} onNavigate={goPage} onLogout={logout} />;
    }
    // Toute autre page (fonctions commerçants comprises) : menu principal
    return <Home user={user} onLogout={logout} onNavigate={goPage} />;
  }

  if (checkoutPlan) {
    return (
      <Checkout
        plan={checkoutPlan}
        user={user}
        onBack={() => navigate({ checkoutPlan: null, showPricing: true })}
        onGoHome={() => navigate({ checkoutPlan: null, showPricing: false, page: "landing" })}
        onGoLogin={() => navigate({ checkoutPlan: null, showPricing: false, showLogin: true })}
        onGoGestion={() => navigate({ checkoutPlan: null, showPricing: false, page: "home" })}
        onLogout={logout}
        onMenuNavigate={goMenu}
      />
    );
  }

  if (showPricing) {
    return (
      <Pricing
        user={user}
        onBack={() => navigate({ showPricing: false })}
        onSelectPlan={(p) => navigate({ checkoutPlan: p, showPricing: false })}
        onGoLogin={() => navigate({ showPricing: false, showLogin: true })}
        onGoGestion={() => navigate({ showPricing: false, page: "home" })}
        onLogout={logout}
        onMenuNavigate={goMenu}
      />
    );
  }

  if (!user && showLogin) {
    return (
      <Login
        onLogin={(u) => { setUser(u); navigate({ page: "home", showLogin: false }); }}
        onBack={() => navigate({ showLogin: false })}
      />
    );
  }

  if (!user || page === "landing") {
    return (
      <LandingPage
        user={user}
        onGoLogin={() => navigate({ showLogin: true })}
        onGoStart={() => navigate({ showPricing: true })}
        onGoGestion={() => navigate({ page: "home" })}
        onLogout={logout}
        onMenuNavigate={goMenu}
      />
    );
  }

  const isAdmin = user.role === "Admin";
  // Fonctionnalités des plans payants : personnalisation et statistiques
  const hasPremiumPlan =
    !!user.organization_id && (isAdmin || ["Premium", "Extra"].includes(user.plan));
  // Scénarios d'e-mails automatiques : plan Extra uniquement
  const hasExtraPlan =
    !!user.organization_id && (isAdmin || user.plan === "Extra");

  if (page === "admin" && isAdmin) {
    return <AdminDashboard user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "users" && isAdmin) {
    return <UserManagement user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "cards" && isAdmin) {
    return <CardManagement user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "organizations" && isAdmin) {
    return <OrgManagement user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "generate-qr") {
    return <GenerateQR user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "customize" && hasPremiumPlan) {
    return <CardCustomization user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "mail-scenarios" && hasExtraPlan) {
    return <MailScenarios user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "scan") {
    return <ScanCard user={user} onNavigate={goPage} onLogout={logout} />;
  }
  // Sous-comptes employés : réservé au gérant (un Staff ne gère pas de comptes)
  if (page === "staff" && user.role === "User" && user.organization_id) {
    return <StaffManagement user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "stats" && hasPremiumPlan) {
    return <StatsDashboard user={user} onNavigate={goPage} onLogout={logout} />;
  }
  if (page === "qr-links" && isAdmin) {
    return <QrLinks user={user} onNavigate={goPage} onLogout={logout} />;
  }
  // Banc d'essai cartes nouvelle génération : admin uniquement
  if (page === "test-card" && isAdmin) {
    return <TestCard user={user} onNavigate={goPage} onLogout={logout} />;
  }
  // Banc d'essai du prélèvement SEPA : admin uniquement
  if (page === "billing-test" && isAdmin) {
    return <BillingTest user={user} onNavigate={goPage} onLogout={logout} />;
  }
  // Factures Stripe : admin uniquement
  if (page === "invoices" && isAdmin) {
    return <Invoices user={user} onNavigate={goPage} onLogout={logout} />;
  }
  // Codes de réduction Stripe : admin uniquement
  if (page === "coupons" && isAdmin) {
    return <Coupons user={user} onNavigate={goPage} onLogout={logout} />;
  }
  // Facturations du commerce (menu burger) : gérant uniquement
  if (page === "billing" && user.role === "User" && user.organization_id) {
    return <Billing user={user} onNavigate={goPage} onLogout={logout} />;
  }
  // Abonnement (menu burger) : la page des offres, avec le tunnel de commande
  if (page === "subscription" && user.role === "User") {
    return (
      <Pricing
        user={user}
        onBack={() => goPage("home")}
        onSelectPlan={(p) => navigate({ checkoutPlan: p, page: "home" })}
        onGoLogin={() => {}}
        onGoGestion={() => goPage("home")}
        onLogout={logout}
        onMenuNavigate={goPage}
      />
    );
  }
  if (page === "account") {
    return (
      <MyAccount
        user={user}
        onNavigate={goPage}
        onLogout={logout}
        onUserUpdate={setUser}
        onUpgrade={() => navigate({ showPricing: true })}
      />
    );
  }

  return (
    <Home
      user={user}
      onLogout={logout}
      onNavigate={goPage}
      // Pop-up d'offres des comptes en attente : ouvre le tunnel de commande
      onSelectPlan={(p) => navigate({ checkoutPlan: p })}
    />
  );
}

export default App;
